// @flow

import type { LogicElement, WizardPageData } from '@chatchamp/chatchamp-types'

import { delay } from '../../common'
import {
  createClickout,
  getCustomerConfig,
  getRecommendedProducts,
  getWizard,
  getWizardPage,
  getWizardPages,
  saveAndGetNextPage
} from '../../common/api/chatchampApi'
import type { QuestionSelection } from '../../common/wizard'
import { executeJavascript } from '../../common/javascript'
import type { Notification, ScrollDirection } from './state'

type ActionParams = {
  commit: any,
  state: any,
  getters: any,
  dispatch: any
}

export const actions = {
  async getCustomerConfig ({
    commit,
    state
  }: { commit: any, state: any }) {
    const config = await getCustomerConfig(state.customerId, state.subscriberId, `referrer=${state.parentUrl}`)

    commit('setCustomerConfig', config.chatBubble)
  },
  async loadWizard ({
    commit,
    state
  }: ActionParams) {
    const wizard = await getWizard(state.customerId, state.subscriberId, state.integrationId)
    if (wizard.skipLandingPage) commit('setIsLandingPage', false)
    commit('setWizard', wizard)
  },
  async loadMoreProducts ({
    commit,
    state,
    getters
  }: ActionParams): Promise<void> {
    commit('setIsLoadingProducts', true)

    const { alreadySeenProductIds } = state.recommendedProducts
    const newProducts = await getRecommendedProducts(state.customerId, state.subscriberId, {
      pageId: getters.currentWizardPage.id,
      sort: state.sort,
      alreadySeenProductIds
    })
    newProducts.elements = newProducts.products
    delete newProducts.products

    const products = Object.assign({}, state.recommendedProducts, newProducts)
    products.elements = state.recommendedProducts.elements.concat(newProducts.elements)
    commit('setRecommendedProducts', products)
    commit('setIsLoadingProducts', false)
    commit('setLatestLoadedProducts', newProducts.elements)
  },
  async loadWizardPages ({
    commit,
    state,
    getters
  }: ActionParams): Promise<void> {
    const wizardPages = await getWizardPages(state.customerId, getters.wizardPayload)
    commit('setWizardPages', wizardPages)
  },
  async loadWizardPage ({
    commit,
    state,
    getters,
    dispatch
  }: ActionParams, wizardPageIndex: number): Promise<void> {
    commit('setWizardPageData', null)
    commit('setWizardPageIndex', wizardPageIndex)
    const wizardPageData = await getWizardPage(state.customerId, state.subscriberId, getters.wizardPayload, getters.currentWizardPage.id)
    const wizardPageIndexConfirmation = state.wizardPages.findIndex(({ id }) => id === wizardPageData.wizardPageId)
    if (wizardPageIndexConfirmation > -1 && wizardPageIndexConfirmation !== wizardPageIndex) commit('setWizardPageIndex', wizardPageIndexConfirmation)

    dispatch('onLoadWizardPageData', wizardPageData)
  },
  processLogicElements (_: ActionParams, logicElements: Array<LogicElement>): void {
    if (!logicElements) return

    logicElements.forEach(logicElement => {
      if (logicElement.pageType === 'javascript') {
        executeJavascript(logicElement.javascript)
      }
    })
  },
  showLandingPage ({ commit }: ActionParams) {
    commit('setIsLandingPage', true)
  },
  startWizard ({ commit }: ActionParams) {
    commit('setIsLandingPage', false)
  },
  restartWizard ({
    state,
    dispatch
  }: ActionParams) {
    dispatch('goBackToPage', state.visitedPageIds[0])
  },
  async prevWizardPage ({
    state,
    dispatch
  }: ActionParams) {
    await dispatch('goBackToPage', state.visitedPageIds[state.visitedPageIds.length - 1])
  },
  selectQuestionOption ({
    commit,
    state,
    getters
  }: ActionParams, payload: QuestionSelection) {
    const wizardPageId = getters.currentWizardPage.id
    const { options } = payload
    commit('setQuestionSelections', {
      wizardPageId,
      options
    })
  },
  async saveAndGetNextPage ({
    commit,
    state,
    getters,
    dispatch
  }: ActionParams) {
    dispatch('processLogicElementsAfter')
    if (getters.isLastPage) return
    commit('setIsSavingWizardPageAnswer', true)

    const currentWizardPageId = getters.currentWizardPage.id
    commit('pushVisitedPageId', currentWizardPageId)
    const options = getters.selectedOptions[currentWizardPageId]

    const {
      wizardPageId,
      ...wizardPageData
    } = await saveAndGetNextPage(state.customerId, state.subscriberId, getters.wizardPayload, currentWizardPageId, options)
    dispatch('onLoadWizardPageData', wizardPageData)

    const nextWizardPageIndex = state.wizardPages.findIndex(w => w.id === wizardPageId)
    if (nextWizardPageIndex > -1) commit('setWizardPageIndex', nextWizardPageIndex)

    commit('setIsSavingWizardPageAnswer', false)
  },
  cleanSelectedOptions ({
    commit,
    state
  }: ActionParams, wizardPageId: number) {
    const pageIdsToRemoveOptions = Object.keys(state.selectedOptions).filter(pageId => +pageId !== +wizardPageId && !state.visitedPageIds.includes(+pageId))
    pageIdsToRemoveOptions.forEach(pageId => commit('cleanQuestionSelections', +pageId))
  },
  async goBackToPage ({
    dispatch,
    commit,
    state
  }: ActionParams, wizardPageId: number) {
    commit('setSort', 'utility:desc')

    const visitedPageIndex = state.visitedPageIds.findIndex(pageId => pageId === wizardPageId)
    if (visitedPageIndex === -1) return

    state.visitedPageIds.slice(visitedPageIndex).forEach(pageId => commit('removeVisitedPage', +pageId))
    dispatch('cleanSelectedOptions', wizardPageId)
    const pageIndex = state.wizardPages.findIndex(page => page.id === wizardPageId)
    await dispatch('loadWizardPage', pageIndex)
  },
  async changeSort ({
    commit,
    dispatch
  }: ActionParams, value: string) {
    await dispatch('hideProducts')
    commit('setSort', value)
    await dispatch('loadRecommendedProducts')
    await dispatch('showProducts')
  },
  async hideProducts ({ commit }: ActionParams) {
    commit('setHiddingProducts', true)
    await delay(500)
  },
  async showProducts ({ commit }: ActionParams) {
    commit('setHiddingProducts', false)
    await delay(500)
  },
  async loadRecommendedProducts ({
    commit,
    getters,
    state
  }: ActionParams) {
    const numberOfProducts = state.recommendedProducts.elements.length

    commit('setIsLoadingProducts', true)
    commit('setRecommendedProducts', {
      ...state.recommendedProducts,
      elements: []
    })

    const result = await getRecommendedProducts(state.customerId, state.subscriberId, {
      pageId: getters.currentWizardPage.id,
      sort: state.sort,
      numberOfProducts
    })

    if (result?.products) {
      result.elements = result.products
      delete result.products
      const recommendedProducts = { ...state.recommendedProducts, ...result }
      commit('setRecommendedProducts', recommendedProducts)
    }

    commit('setIsLoadingProducts', false)
  },
  setScrollDirection ({ commit }: ActionParams, direction: ScrollDirection) {
    commit('setScrollDirection', direction)
  },
  setProductAddingToCart ({ commit }: ActionParams, productId: ?string) {
    commit('setProductAddingToCart', productId)
  },
  setRoutineAddingToCart ({ commit }: ActionParams, value: boolean) {
    commit('setRoutineAddingToCart', value)
  },
  addProductToCart ({ commit }: ActionParams, productId: string) {
    commit('addProductToCart', productId)
  },
  setNotification ({ commit }: ActionParams, notification: Notification) {
    commit('setNotification', notification)
  },
  createClickout ({ state, getters }: ActionParams, url: string) {
    createClickout(state.customerId, state.subscriberId, getters.wizardPageData.message.id, { url })
  },
  onLoadWizardPageData ({ commit, dispatch }: ActionParams, wizardPageData: WizardPageData<any>): void {
    commit('setWizardPageData', wizardPageData)
    dispatch('processLogicElementsBefore')
    if (wizardPageData.products) commit('setRecommendedProducts', wizardPageData.products)
  },
  processLogicElementsBefore ({ state, dispatch }: ActionParams): void {
    dispatch('processLogicElements', state.wizardPageData.logicElementsBefore)
  },
  processLogicElementsAfter ({ state, dispatch }: ActionParams): void {
    dispatch('processLogicElements', state.wizardPageData.logicElementsAfter)
  },
  goToLandingPage ({ dispatch }: ActionParams): void {
    dispatch('restartWizard')
    dispatch('showLandingPage')
  }
}
